import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import Badge from 'shopper/components/Badge';
import Icon from 'shopper/components/Icon';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { notificationQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import { getUnreadNotificationTypesCount } from 'services/notification';

import Button from '../../../Shopper/Button';

const NotificationDropdown = dynamic(() => import('./Dropdown'), {
  ssr: false,
});

const NotificationButton = ({ gaEventCategory }) => {
  const notificationsButtonRef = useRef(null);
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const { isLoggedIn, loadingLoggedInUser } = useUser();

  const { data: notificationCounters, isSuccess } = useQuery({
    queryKey: notificationQueryKeys.counts(),
    queryFn: ({ signal }) => getUnreadNotificationTypesCount({ signal }),
    enabled: isLoggedIn,
    refetchOnWindowFocus: true,
  });

  const shouldRenderBadge =
    (!isLoggedIn && !loadingLoggedInUser) ||
    (isSuccess && notificationCounters?.countAll > 0);

  const onNotificationButtonClick = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'notifications_unlogged_open',
        category: gaEventCategory,
      });
    }

    sendEvent({
      action: 'notification_list',
      category: gaEventCategory,
    });
    setNotificationsOpen((prev) => !prev);
  };

  const onNotificationDropdownClose = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'notifications_unlogged_close',
        category: gaEventCategory,
      });
    }

    setNotificationsOpen(false);
  };

  return (
    <div className="relative block">
      <Button
        ref={notificationsButtonRef}
        aria-label="Abrir notificações"
        className={twJoin('relative', isNotificationsOpen && 'z-50')}
        data-test-selector="open-notification-dropdown"
        icon={<Icon name="notification" />}
        onClick={onNotificationButtonClick}
      >
        {shouldRenderBadge && (
          <Badge
            className="absolute right-7 top-0"
            shape="dot"
            type="highlight"
          />
        )}
        {placeholder('actions.openNotifications')}
      </Button>
      {isNotificationsOpen && (
        <NotificationDropdown
          isOpen={isNotificationsOpen}
          openerRef={notificationsButtonRef}
          onClose={onNotificationDropdownClose}
        />
      )}
    </div>
  );
};

NotificationButton.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
};

export default NotificationButton;
